import React, { useEffect } from "react";
import PageIntro from "../components/PageIntro";

function MapMarker({ href, label, className, color = "emerald", isReady = true }) {
  const colorMap = {
    emerald: {
      ping: "bg-emerald-400",
      dot: "bg-emerald-700",
    },
    amber: {
      ping: "bg-amber-400",
      dot: "bg-amber-700",
    },
    red: {
      ping: "bg-red-400",
      dot: "bg-red-700",
    },
    slate: {
      ping: "bg-slate-300",
      dot: "bg-slate-500",
    },
  };

  const c = colorMap[color] ?? colorMap.emerald;

  const markerInner = (
    <>
      <span className="relative flex h-7 w-7 -translate-x-1/2 -translate-y-1/2">
        <span
          className={`absolute inline-flex h-full w-full rounded-full ${c.ping} ${
            isReady ? "animate-ping opacity-50" : "opacity-35"
          }`}
        />
        <span
          className={`relative inline-flex h-7 w-7 rounded-full border-4 border-white ${c.dot} shadow-lg transition ${
            isReady ? "group-hover:scale-110" : ""
          }`}
        />
      </span>

      <span className="pointer-events-none absolute left-1/2 top-0 z-10 hidden -translate-x-1/2 -translate-y-[115%] whitespace-nowrap rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-900 shadow-md opacity-0 transition duration-200 group-hover:opacity-100 md:block">
        {label}
      </span>
    </>
  );

  if (!isReady || href === "#") {
    return (
      <div className={`absolute group ${className}`} aria-label={`${label} noch nicht verfügbar`}>
        {markerInner}
      </div>
    );
  }

  return (
    <a href={href} className={`absolute group ${className}`} aria-label={`${label} öffnen`}>
      {markerInner}
    </a>
  );
}

export default function Bundeslaender() {
  useEffect(() => {
    document.title =
      "Erdwärme nach Bundesland: Genehmigung & Daten | Erdwärme-Experten";

    let meta = document.querySelector("meta[name='description']");
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Übersicht zur Erdwärme in den Bundesländern: Datenlage, Genehmigungen, Regelungen und verfügbare Detailseiten für Bayern, Berlin, Hamburg, Bremen, NRW und mehr."
    );

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://www.erdwaerme-experten.de/bundeslaender";
  }, []);

  const stateCards = [
    {
      name: "Bayern",
      href: "/bundeslaender/bayern",
      badge: "Schon verfügbar",
      dataStatus: "gut",
      text: "Sehr gute Datengrundlage mit UmweltAtlas und Energie-Atlas. Ideal für eine fundierte Vorprüfung von Erdwärme-Systemen.",
    },
    {
      name: "Berlin",
      href: "/bundeslaender/berlin",
      badge: "Schon verfügbar",
      dataStatus: "gut",
      text: "Besonders relevant durch dichte Bebauung, Genehmigungsanforderungen und häufige Einschränkungen durch Wasserschutzgebiete.",
    },
    {
      name: "Hamburg",
      href: "/bundeslaender/hamburg",
      badge: "Neu verfügbar",
      dataStatus: "gut",
      text: "Gute Datengrundlage und klare Leitfäden. Besonders interessant durch urbane Rahmenbedingungen und Grundwasserbezug.",
    },
    {
      name: "Bremen",
      href: "/bundeslaender/bremen",
      badge: "Neu verfügbar",
      dataStatus: "gut",
      text: "Überschaubare Genehmigungsstruktur und solide Datengrundlage – gut geeignet für erste Standortbewertungen.",
    },
    {
      name: "Nordrhein-Westfalen",
      href: "/bundeslaender/nordrhein-westfalen",
      badge: "Neu verfügbar",
      dataStatus: "gut",
      text: "Sehr relevant durch hohe Nachfrage. Gute Datengrundlage, aber oft komplexe Genehmigungssituation im urbanen Raum.",
    },
    {
      name: "Mecklenburg-Vorpommern",
      href: "/bundeslaender/mecklenburg-vorpommern",
      badge: "Neu verfügbar",
      dataStatus: "gut",
      text: "Sehr gute Datengrundlage mit geologischen Karten und WMS-Diensten – gut geeignet für standortbezogene Vorprüfungen.",
    },
    {
      name: "Baden-Württemberg",
      href: "/bundeslaender/baden-wuerttemberg",
      badge: "Neu verfügbar",
      dataStatus: "teilweise",
      text: "Teilweise gute Datengrundlage, regional aber nicht überall gleich vollständig. Genehmigungen sind häufig stark standortabhängig.",
    },
    {
      name: "Brandenburg",
      href: "#",
      badge: "Später",
      dataStatus: "eingeschränkt",
      text: "Wichtige Tiefendaten zur Wärmeleitfähigkeit fehlen derzeit – daher nur eingeschränkte Vorprüfung möglich.",
    },
    {
      name: "Sachsen",
      href: "#",
      badge: "Später",
      dataStatus: "gut",
      text: "Gute Datengrundlage für erste Machbarkeitsabschätzungen und Vergleich unterschiedlicher Systeme.",
    },
    {
      name: "Schleswig-Holstein",
      href: "#",
      badge: "Später",
      dataStatus: "gut",
      text: "Gute Datenbasis mit Fokus auf Grundwasser und Küstenbedingungen – wichtige Faktoren für die Planung.",
    },
    {
      name: "Thüringen",
      href: "#",
      badge: "Später",
      dataStatus: "gut",
      text: "Solide Datengrundlage für technische Vorprüfungen und erste wirtschaftliche Einschätzungen.",
    },
    {
      name: "Hessen",
      href: "#",
      badge: "Später",
      dataStatus: "eingeschränkt",
      text: "Derzeit fehlen wichtige Tiefendaten zur Wärmeleitfähigkeit für eine belastbare Standortbewertung.",
    },
    {
      name: "Niedersachsen",
      href: "#",
      badge: "Später",
      dataStatus: "eingeschränkt",
      text: "Keine flächendeckend verfügbaren Tiefendaten – daher nur eingeschränkte Aussagekraft für Erdwärme.",
    },
    {
      name: "Rheinland-Pfalz",
      href: "#",
      badge: "Später",
      dataStatus: "eingeschränkt",
      text: "Aktuell eingeschränkte Datengrundlage für die standortbezogene Bewertung von Erdwärme.",
    },
    {
      name: "Saarland",
      href: "#",
      badge: "Später",
      dataStatus: "eingeschränkt",
      text: "Nur eingeschränkte Vorprüfung möglich, da wichtige Untergrunddaten fehlen.",
    },
    {
      name: "Sachsen-Anhalt",
      href: "#",
      badge: "Später",
      dataStatus: "eingeschränkt",
      text: "Fehlende Tiefendaten erschweren aktuell eine belastbare Einschätzung der Erdwärme-Eignung.",
    },
  ];

  const dataLegend = [
    {
      key: "gut",
      label: "Gute Datengrundlage",
      color: "bg-emerald-100 text-emerald-800 border-emerald-200",
      dot: "bg-emerald-500",
      description: "Standortbezogene Vorprüfung mit guter Datenbasis möglich.",
    },
    {
      key: "teilweise",
      label: "Teilweise Datengrundlage",
      color: "bg-amber-100 text-amber-800 border-amber-200",
      dot: "bg-amber-500",
      description: "Nutzbar, aber nicht in allen Regionen gleich gut verfügbar.",
    },
    {
      key: "eingeschränkt",
      label: "Eingeschränkte Datengrundlage",
      color: "bg-rose-100 text-rose-800 border-rose-200",
      dot: "bg-rose-500",
      description: "Wichtige Tiefendaten fehlen aktuell, daher nur eingeschränkte Vorprüfung.",
    },
  ];

  const getDataBadge = (status) => {
    switch (status) {
      case "gut":
        return {
          label: "Datengrundlage gut",
          className: "bg-emerald-50 text-emerald-700",
        };
      case "teilweise":
        return {
          label: "Datengrundlage teilweise",
          className: "bg-amber-50 text-amber-700",
        };
      case "eingeschränkt":
        return {
          label: "Datengrundlage eingeschränkt",
          className: "bg-rose-50 text-rose-700",
        };
      default:
        return {
          label: "Datengrundlage offen",
          className: "bg-slate-100 text-slate-600",
        };
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-[#f6f3f2] pb-20 pt-14 text-slate-900 sm:pt-20">
      <PageIntro
        eyebrow="Bundesländer-Übersicht"
        title={
          <>
            Erdwärme in Deutschland{" "}
            <span className="text-red-700">nach Bundesland</span>
          </>
        }
        description={
          <>
            Jedes Bundesland hat eigene praktische Rahmenbedingungen – von
            Genehmigungen und Leitfäden bis zur Datengrundlage für die
            standortbezogene Vorprüfung. Hier finden Sie einen schnellen Überblick,
            welche Bundesländer bereits eigene Detailseiten haben und wo die
            Datenbasis für einen GeoReport besonders gut ist.
          </>
        }
        className="mb-12"
      >
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {[
            ["1. Bundesland wählen", "Einstieg über Deutschlandkarte oder Kartenübersicht"],
            ["2. Datenlage verstehen", "Schnell erkennen, wo die Vorprüfung besonders belastbar ist"],
            ["3. Zur Detailseite", "Von dort direkt zum GeoReport oder zu den Standortinfos"],
          ].map(([title, text]) => (
            <div
              key={title}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="text-sm font-semibold text-slate-500">{title}</div>
              <div className="mt-2 font-medium text-slate-800">{text}</div>
            </div>
          ))}
        </div>
      </PageIntro>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-14">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-8 items-start">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 md:p-8 shadow-lg overflow-hidden">
            <div className="flex items-center justify-between gap-4 mb-5">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
                  Deutschlandkarte & verfügbare Seiten
                </h2>
                <p className="mt-2 text-slate-600 max-w-2xl">
                  Erste Bundesländer sind bereits detailliert aufbereitet.
                  Schrittweise kommen weitere hinzu – mit Fokus auf Datengrundlage,
                  Genehmigung und praktischer Umsetzbarkeit.
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-sm text-slate-600">
                <span className="h-2.5 w-2.5 rounded-full bg-red-600 inline-block" />
                Detailseite verfügbar
              </div>
            </div>

            <div className="relative rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 p-4 md:p-6">
              <img loading="lazy" decoding="async"
                src="/germany.svg"
                alt="Deutschlandkarte mit Bundesländern"
                className="w-full h-auto rounded-2xl border border-slate-200 bg-white p-4"
              />

              <MapMarker
                href="/bundeslaender/baden-wuerttemberg"
                label="Baden-Württemberg"
                className="left-[37%] top-[81%]"
                color="amber"
                isReady={true}
              />

              <MapMarker
                href="/bundeslaender/bayern"
                label="Bayern"
                className="left-[61%] top-[75%]"
                color="emerald"
                isReady={true}
              />

              <MapMarker
                href="/bundeslaender/berlin"
                label="Berlin"
                className="left-[77.5%] top-[36%]"
                color="emerald"
                isReady={true}
              />

              <MapMarker
                href="/bundeslaender/brandenburg"
                label="Brandenburg"
                className="left-[79%] top-[42%]"
                color="red"
                isReady={true}
              />

              <MapMarker
                href="/bundeslaender/bremen"
                label="Bremen"
                className="left-[35%] top-[29%]"
                color="emerald"
                isReady={true}
              />

              <MapMarker
                href="/bundeslaender/hamburg"
                label="Hamburg"
                className="left-[45%] top-[23%]"
                color="emerald"
                isReady={true}
              />

              <MapMarker
                href="/bundeslaender/nordrhein-westfalen"
                label="Nordrhein-Westfalen"
                className="left-[25%] top-[47%]"
                color="emerald"
                isReady={true}
              />

              <MapMarker
                href="/bundeslaender/mecklenburg-vorpommern"
                label="Mecklenburg-Vorpommern"
                className="left-[66%] top-[22%]"
                color="emerald"
                isReady={true}
              />

              <MapMarker
                href="#"
                label="Schleswig-Holstein"
                className="left-[46%] top-[17%]"
                color="emerald"
                isReady={false}
              />

              <MapMarker
                href="#"
                label="Niedersachsen"
                className="left-[44%] top-[36%]"
                color="red"
                isReady={false}
              />

              <MapMarker
                href="#"
                label="Hessen"
                className="left-[37%] top-[57%]"
                color="red"
                isReady={false}
              />

              <MapMarker
                href="#"
                label="Rheinland-Pfalz"
                className="left-[21%] top-[65%]"
                color="red"
                isReady={false}
              />

              <MapMarker
                href="#"
                label="Saarland"
                className="left-[20%] top-[73%]"
                color="red"
                isReady={false}
              />

              <MapMarker
                href="#"
                label="Sachsen-Anhalt"
                className="left-[61.5%] top-[42%]"
                color="red"
                isReady={false}
              />

              <MapMarker
                href="#"
                label="Sachsen"
                className="left-[76%] top-[53%]"
                color="emerald"
                isReady={false}
              />

              <MapMarker
                href="#"
                label="Thüringen"
                className="left-[55%] top-[56%]"
                color="emerald"
                isReady={false}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-xl font-bold">Datengrundlage je Bundesland</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Die Vorprüfung ist nicht in allen Bundesländern gleich gut möglich.
                Besonders relevant ist, ob standortbezogene Tiefendaten wie die
                Wärmeleitfähigkeit des Untergrunds verfügbar sind.
              </p>

              <div className="mt-5 space-y-3">
                {dataLegend.map((item) => (
                  <div
                    key={item.key}
                    className={`rounded-2xl border p-4 ${item.color}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`h-3 w-3 rounded-full ${item.dot}`} />
                      <span className="font-semibold">{item.label}</span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-xl font-bold">So nutzt du die Übersicht am besten</h3>
              <div className="mt-5 space-y-3">
                {[
                  "Bundesland auswählen und die regionale Situation besser verstehen",
                  "Datengrundlage einschätzen: gut, teilweise oder eingeschränkt",
                  "Von der Detailseite direkt zum GeoReport oder zur weiteren Vorprüfung wechseln",
                ].map((step, i) => (
                  <div
                    key={step}
                    className="flex items-start gap-3 rounded-2xl bg-slate-50 border border-slate-200 p-4"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-700 text-sm font-bold text-white">
                      {i + 1}
                    </div>
                    <p className="text-slate-700 leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-20">
        <div className="flex items-end justify-between gap-4 mb-7">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">Bundesländer im Überblick</h2>
            <p className="mt-2 text-slate-600 max-w-3xl">
              Die Karten zeigen, in welchen Bundesländern bereits Detailseiten
              verfügbar sind und wo die Datengrundlage für eine standortbezogene
              Vorprüfung besonders gut oder eingeschränkt ist.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
          {stateCards.map((state) => {
            const isReady = state.href !== "#";
            const Wrapper = isReady ? "a" : "div";
            const dataBadge = getDataBadge(state.dataStatus);

            return (
              <Wrapper
                key={state.name}
                {...(isReady ? { href: state.href } : {})}
                className={`group rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition ${
                  isReady ? "hover:shadow-lg hover:-translate-y-0.5 cursor-pointer" : "opacity-90"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-xl font-bold text-slate-900 group-hover:text-red-700 transition">
                    {state.name}
                  </h3>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      isReady ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {state.badge}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${dataBadge.className}`}
                  >
                    {dataBadge.label}
                  </span>
                </div>

                <p className="mt-4 text-slate-600 leading-relaxed">{state.text}</p>

                <div className="mt-6 text-sm font-semibold text-slate-800 group-hover:text-red-700 transition">
                  {isReady ? "Zur Landesübersicht →" : "Detailseite folgt"}
                </div>
              </Wrapper>
            );
          })}
        </div>
      </section>
    </div>
  );
}
